// Public guest endpoints only. No credentials belong in this configuration.
export const bookingEvent = Object.freeze({ username: 'algorithmic.network', slug: 'get-in-touch', duration: 30, url: 'https://cal.com/algorithmic.network/get-in-touch' });
export class BookingError extends Error {
  constructor(kind, uncertain = false) { super(kind); this.kind = kind; this.uncertain = uncertain; }
}
export function dateKey(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, calendar: 'gregory', numberingSystem: 'latn', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const part = type => parts.find(value => value.type === type).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}
export function monthDate(year, month, day = 1) { return new Date(Date.UTC(year, month, day, 12)); }
export function monthKey(date) { return date.toISOString().slice(0, 7); }
export function validTimeZone(value) { try { new Intl.DateTimeFormat('en', { timeZone: value }).format(); return true; } catch { return false; } }
export function safeMeetingUrl(value) {
  try { const url = new URL(value); return url.protocol === 'https:' && ['cal.com', 'app.cal.com'].includes(url.hostname) && !url.port && !url.username && !url.password ? url.href : null; } catch { return null; }
}
async function request(path, { method = 'GET', body, signal, fetcher = fetch } = {}) {
  const controller = new AbortController(), abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true }); if (signal?.aborted) abort();
  const timeout = setTimeout(abort, 25000), mutating = method === 'POST';
  try {
    const response = await fetcher(`https://api.cal.com${path}`, {
      method, credentials: 'omit', cache: 'no-store', signal: controller.signal,
      headers: { 'cal-api-version': path.startsWith('/v2/slots') ? '2024-09-04' : '2026-02-25', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (response.status === 429) throw new BookingError('rateLimit');
    if (response.status >= 500) throw new BookingError('failed', mutating);
    let result; try { result = await response.json(); } catch { throw new BookingError('failed', mutating); }
    if (!response.ok || result.status !== 'success') {
      const message = result.error?.message ?? result.message ?? '';
      const conflict = response.status === 409 || /no available|not available|unavailable|already booked|already been booked/i.test(String(message));
      throw new BookingError(conflict ? 'unavailable' : 'failed');
    }
    return result.data;
  } catch (error) {
    if (error instanceof BookingError) throw error;
    if (signal?.aborted) throw new BookingError('aborted');
    // A lost POST response may still be a confirmed booking. Never retry it.
    throw new BookingError('failed', mutating);
  } finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
}
export async function availableSlots(month, timeZone, options = {}) {
  if (!validTimeZone(timeZone)) throw new BookingError('timezone');
  const year = month.getUTCFullYear(), index = month.getUTCMonth();
  const query = new URLSearchParams({ username: bookingEvent.username, eventTypeSlug: bookingEvent.slug,
    start: monthDate(year, index, 0).toISOString().slice(0, 10), end: monthDate(year, index + 1, 2).toISOString().slice(0, 10), timeZone });
  const data = await request(`/v2/slots?${query}`, options);
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new BookingError('failed');
  const now = options.now ?? Date.now(), grouped = new Map(), unique = new Set();
  for (const list of Object.values(data)) {
    if (!Array.isArray(list)) throw new BookingError('failed');
    for (const slot of list) {
      const instant = new Date(slot?.start);
      if (!Number.isFinite(+instant)) throw new BookingError('failed');
      const start = instant.toISOString(), key = dateKey(instant, timeZone);
      if (+instant <= now || !key.startsWith(monthKey(month)) || unique.has(start)) continue;
      unique.add(start); if (!grouped.has(key)) grouped.set(key, []); grouped.get(key).push(start);
    }
  }
  for (const slots of grouped.values()) slots.sort();
  return grouped;
}
export async function createBooking({ start, name, email, topic, notes = '', timeZone, language }, options = {}) {
  if (!validTimeZone(timeZone) || !Number.isFinite(Date.parse(start)) || !name.trim() || !email.trim() || !topic.trim()) throw new BookingError('failed');
  if (Date.parse(start) <= Date.now()) throw new BookingError('unavailable');
  const data = await request('/v2/bookings', { ...options, method: 'POST', body: {
    start: new Date(start).toISOString(), eventTypeSlug: bookingEvent.slug, username: bookingEvent.username,
    attendee: { name: name.trim(), email: email.trim(), timeZone, language },
    bookingFieldsResponses: { title: topic.trim(), notes: notes.trim() },
    // No hidden enrichment consent, account credentials or additional guests.
  } });
  if (!data || typeof data.uid !== 'string' || !data.uid || Date.parse(data.start) !== Date.parse(start) || !['accepted', 'pending'].includes(data.status)) throw new BookingError('failed', true);
  return { uid: data.uid, status: data.status, start: data.start, end: data.end, meetingUrl: safeMeetingUrl(data.meetingUrl || data.location) };
}
